import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { reactRouterBrowserTracingIntegration, type init } from '@sentry/react';
import { scrubRequestContent } from '@immo/observability';
import { env } from '@/lib/env';

type SentryInitOptions = Parameters<typeof init>[0];

const DEFAULT_TRACES_SAMPLE_RATE = 1.0;

function parseTracesSampleRate(raw: string | undefined): number {
  if (raw === undefined) return DEFAULT_TRACES_SAMPLE_RATE;
  const parsed = Number.parseFloat(raw);

  return Number.isFinite(parsed) ? parsed : DEFAULT_TRACES_SAMPLE_RATE;
}

export function buildSentryInitOptions(dsn: string): SentryInitOptions {
  return {
    dsn,
    environment: env.mode,
    release: env.sentry.release,
    tracesSampleRate: parseTracesSampleRate(env.sentry.tracesSampleRate),
    // Trace-propagation headers (`sentry-trace`, `baggage`) go to the API
    // origin ONLY — never to arbitrary third-party origins, so trace ids are
    // not leaked. An explicit allow-list, never a wildcard.
    tracePropagationTargets: [env.apiOrigin],
    integrations: [
      // The piece that was missing: without a browser tracing integration the
      // configured tracesSampleRate has been sampling an empty stream. Records
      // pageload/navigation transactions (named by matched route pattern, not
      // URL) and http.client spans for API calls. React Router v7 data-router
      // instrumentation — paired with wrapCreateBrowserRouter in routes.tsx.
      reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    sendDefaultPii: false,
    beforeSend: scrubRequestContent,
    beforeSendTransaction: scrubRequestContent,
  };
}
